import { useEffect, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import type { MediaSummary, StructuredFilter } from "@media-viewer/shared";
import { MediaTile } from "../components/MediaTile";
import { useSelection } from "../components/SelectionContext";
import { SelectionToolbar, SelectModeToggle } from "../components/SelectionToolbar";
import { SearchBar } from "../components/SearchBar";
import { filterFromUrlState, filterToUrlState } from "../search/filter-to-params";

interface HomeProps {
  isAdmin: boolean;
  homeRefreshSeconds: number;
}

const RANDOM_PAGE_SIZE = 120;

function initialFilter(): StructuredFilter | null {
  try {
    const param = new URLSearchParams(window.location.search).get("filter");
    return filterFromUrlState(param);
  } catch {
    return null;
  }
}

// Bucket "now" into intervals of `seconds`. Same bucket → same shuffle order.
// We add a small per-tab nonce so different tabs don't fight over the seed,
// but within a single tab the seed is stable until the next interval — so
// re-mounts (e.g. after navigating back) don't reshuffle.
function bucketSeed(seconds: number, manualOffset: number): string {
  if (seconds <= 0) return `static-${manualOffset}`;
  const bucket = Math.floor(Date.now() / (seconds * 1000));
  return `${bucket}-${manualOffset}`;
}

export function Home({ isAdmin, homeRefreshSeconds }: HomeProps) {
  const sel = useSelection();
  const [filter, setFilterState] = useState<StructuredFilter | null>(initialFilter);

  // Persist filter into the URL so back-button and deep-links work.
  const setFilter = (next: StructuredFilter | null) => {
    setFilterState(next);
    try {
      const u = new URL(window.location.href);
      const encoded = filterToUrlState(next);
      if (encoded.filter) {
        u.searchParams.set("filter", encoded.filter);
      } else {
        u.searchParams.delete("filter");
      }
      window.history.replaceState(null, "", u.toString());
    } catch {
      /* ignore */
    }
  };

  // Manual "Shuffle now" bumps this — same effect as the next bucket tick.
  const [manualOffset, setManualOffset] = useState(0);
  // Once a filter is active we drop random/auto-refresh — the user is hunting
  // for something specific and a periodic shuffle would be hostile.
  const hasFilter = filter !== null && Object.entries(filter).some(([k, v]) => {
    if (k === "rawQuery" || k === "warnings") return false;
    if (Array.isArray(v)) return v.length > 0;
    if (v && typeof v === "object") return Object.values(v).some((x) => x != null);
    return v != null;
  });
  const useRandom = !hasFilter && homeRefreshSeconds >= 0;
  const refetchMs = !hasFilter && homeRefreshSeconds > 0 ? homeRefreshSeconds * 1000 : false;
  const seed = useRandom ? bucketSeed(homeRefreshSeconds, manualOffset) : undefined;

  const q = useQuery({
    queryKey: [
      "media",
      "home",
      {
        filter,
        order: useRandom ? "random" : "recent",
        seed,
      },
    ],
    queryFn: () =>
      api.listMedia({
        limit: RANDOM_PAGE_SIZE,
        order: useRandom ? "random" : undefined,
        seed,
        structured: filter,
      }),
    // Auto-shuffle the All page every N seconds. False disables.
    refetchInterval: refetchMs,
    // Keep the rendered list stable between explicit shuffles. Without these
    // overrides, RQ refetches on remount/focus and (with order=random) the
    // page reshuffles every time the user tabs back or returns from a detail.
    staleTime: refetchMs || 60_000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    // Keep previous results on screen while the next query (new search
    // text, shuffle, etc.) fetches — same reason as MediaGrid. Without
    // it isPending fires on every keystroke past the search threshold,
    // and the top-level loading branch used to unmount the search input
    // and steal focus on mobile.
    placeholderData: keepPreviousData,
  });

  const items: MediaSummary[] = q.data?.items ?? [];

  useEffect(() => {
    sel.setVisibleIds(items.map((i) => i.id));
  }, [items, sel]);

  // Shell (toolbar / search / sort) renders unconditionally — only the
  // body section swaps for loading/error/empty/grid. Otherwise a
  // top-level `if (q.isPending) return <Loading>` unmounts the search
  // input on every new query key, stealing focus on mobile (the
  // on-screen keyboard closes after each keystroke past the live-
  // search threshold).
  return (
    <>
      <SelectionToolbar isAdmin={isAdmin} hasFilter={false} />
      <SearchBar filter={filter} setFilter={setFilter} pinnedKind={null} />
      <div className="flex flex-wrap items-center gap-3 px-4 pt-3 text-xs text-zinc-500">
        <SelectModeToggle />
        {useRandom && (
          <>
            <span>
              ⟳ shuffled
              {refetchMs ? ` · auto-refresh every ${formatInterval(homeRefreshSeconds)}` : ""}
            </span>
            <button
              onClick={() => setManualOffset((n) => n + 1)}
              className="rounded bg-zinc-800 px-2 py-1 hover:bg-zinc-700"
            >
              Shuffle now
            </button>
          </>
        )}
      </div>
      <div className="p-4">
        {q.isPending ? (
          <div className="p-4 text-zinc-500">Loading…</div>
        ) : q.isError ? (
          <div className="p-4 text-red-300">Error: {String(q.error)}</div>
        ) : items.length === 0 ? (
          <div className="rounded bg-zinc-800/40 px-3 py-2 text-xs text-zinc-400">
            {hasFilter
              ? "No media match this search."
              : "Your library is empty. Drop files into the bind-mount or upload some."}
          </div>
        ) : (
          <div className="columns-2 gap-3 sm:columns-3 md:columns-4 lg:columns-5 xl:columns-6">
            {items.map((item, i) => (
              <MediaTile
                key={item.id}
                item={item}
                setParam="all"
                isAdmin={isAdmin}
                index={i}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function formatInterval(s: number): string {
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  return `${Math.round((s / 3600) * 10) / 10}h`;
}
